import { useCallback, useEffect, useRef, useState } from 'react'
import { useToast } from '@chakra-ui/react'
import {
    useDeleteIntegrationTokenMutation,
    useGetIntegrationTokenQuery,
    useGetUserAuthorizeUrlMutation
} from '../../../features/account/accountApi'

const POPUP_POLL_MS = 500
const useRefreshToken = (clientId) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
    const [isAwaitingPopup, setIsAwaitingPopup] = useState(false)
    const pollTimer = useRef(null)
    const toast = useToast()

    const notify = useCallback((title, description, status) => {
        toast({ title, description, status, duration: 3000, isClosable: true, position: 'top-right' })
    }, [toast])

    const tokenQuery = useGetIntegrationTokenQuery(clientId, {
        skip: !isOpen || !clientId,
        refetchOnMountOrArgChange: true
    })
    const [deleteToken, { isLoading: isDeleting }] = useDeleteIntegrationTokenMutation()
    const [getAuthorizeUrl, { isLoading: isRequestingUrl }] = useGetUserAuthorizeUrlMutation()
    const token = (isOpen && tokenQuery.data) || ''

    useEffect(() => {
        if (isOpen && tokenQuery.isError) {
            notify('Error', 'Unable to fetch the token', 'error')
        }
    }, [isOpen, tokenQuery.isError, notify])

    const stopPolling = useCallback(() => {
        if (pollTimer.current) {
            clearInterval(pollTimer.current)
            pollTimer.current = null
        }
    }, [])

    useEffect(() => stopPolling, [stopPolling])

    const open = useCallback(() => {
        setIsOpen(true)
        setIsConfirmingDelete(false)
    }, [])

    const close = useCallback(() => {
        stopPolling()
        setIsAwaitingPopup(false)
        setIsOpen(false)
        setIsConfirmingDelete(false)
    }, [stopPolling])

    const copy = useCallback(() => {
        navigator.clipboard.writeText(token)
            .then(() => notify('Success', 'Token copied to clipboard', 'success'))
            .catch(() => notify('Error', 'Unable to copy the token', 'error'))
    }, [token, notify])

    const requestDelete = useCallback(() => setIsConfirmingDelete(true), [])
    const cancelDelete = useCallback(() => setIsConfirmingDelete(false), [])

    const confirmDelete = useCallback(() => {
        deleteToken(clientId)
        .unwrap()
        .then(() => notify('Success', 'Token has been deleted', 'success'))
        .catch(() => notify('Error', 'Failed to delete token', 'error'))
        .finally(() => setIsConfirmingDelete(false))
    }, [clientId, deleteToken, notify])

    const generate = useCallback(() => {
        getAuthorizeUrl()
        .unwrap()
        .then((url) => {
            const popup = window.open(url, '', 'width=500,height=500')
            if (!popup) {
                notify('Error', 'Allow pop-ups for this site to authorize', 'error')
                return
            }
            setIsAwaitingPopup(true)
            stopPolling()
            pollTimer.current = setInterval(() => {
                if (!popup.closed) {
                    return
                }
                stopPolling()
                tokenQuery.refetch()
                    .unwrap()
                    .then((value) => {
                        if (value) {
                            notify('Success', 'New token loaded', 'success')
                        }
                    })
                    .catch(() => {})
                    .finally(() => setIsAwaitingPopup(false))
                }, POPUP_POLL_MS)
            })
            .catch(() => notify('Error', 'Failed to start authorization', 'error'))
    }, [getAuthorizeUrl, notify, stopPolling, tokenQuery])

    return {
        isOpen,
        token,
        hasToken: Boolean(token),
        isLoading: isOpen && tokenQuery.isFetching && !isAwaitingPopup,
        isGenerating: isRequestingUrl || isAwaitingPopup,
        isDeleting,
        isConfirmingDelete,
        open,
        close,
        copy,
        requestDelete,
        cancelDelete,
        confirmDelete,
        generate
    }
}

export default useRefreshToken
